import type { Campaign } from '@feathr/blackbox';
import { CampaignClass, CampaignState } from '@feathr/blackbox';
import { useFlags } from '@feathr/extender/state';

import type { IUseCampaignModalsReturn } from './useCampaignModals';
import useCampaignModals from './useCampaignModals';
import useCampaignPermissions from './useCampaignPermissions';
import useCampaignText from './useCampaignText';
import { useMinPackageTooltip } from './useMinPackageTooltip';

interface ICampaignText {
  disableRepublish: string | undefined;
  disableDuplicate: string | undefined;
  disableStop: string | undefined;
  disableEdit: string | undefined;
}

interface IUseCampaignStateReturn extends IUseCampaignModalsReturn {
  allowed: {
    /**
     * The logic associated with whether the stop button should be displayed for the
     * current campaign because the campaign lacks the ability to be stopped. Campaigns
     * that have the ability to be disabled/stopped should never have this enabled and
     * should instead modify the logic to disable the button based on specific conditions.
     */
    stop: boolean;
    delete: boolean;
    reportLinkInWizardHeader: boolean;
  };
  /**
   * The logic associated with when to disable the following buttons, generally due to
   * state and/or date constraints.
   */
  disabled: {
    stop: boolean;
    republish: boolean;
    edit: boolean;
    duplicate: boolean;
  };
  /**
   * Whether or not the campaign has a wizard associated with it. Used to help determine
   * when to show the edit button for the wizard.
   */
  hasWizard: boolean;
  /** Whether this is a legacy Meta campaign type (Facebook/EmailListFacebook) with metaV2 enabled. */
  isDeprecatedMetaCampaign: boolean;
  text: ICampaignText;
  includedInLicense: boolean;
}

/**
 * The useCampaign hook allows ups to keep track of important campaign state, types, modals and text.
 * This allows us to use consistent patterns and language when it comes to managing campaigns. It can
 * serve as a single source of truth for which campaigns and under what circumstances they can be stopped,
 * re-published, etc.
 *
 * This lets us stop checking campaign state across the app and instead return contextual allow, disable,
 * etc logic that can be shared across the app, to maintain consistency. We'll slowly roll this out as
 * we touch different parts of the app.
 */
function useCampaign({ campaign }: { campaign: Campaign }): IUseCampaignStateReturn {
  // Get all the campaign-based text
  const text = useCampaignText();
  const { metaV2 } = useFlags();
  const includedInLicense = useCampaignPermissions(campaign.get('_cls'));

  // Get all the modal states and handlers to pass to the return
  const { modalStates, modalHandlers } = useCampaignModals();

  const state = campaign.get('state');
  const type = campaign.get('_cls');
  const { isPastEndDate, isPastStartDate } = campaign;

  // General campaign state
  const isDraft = state === CampaignState.Draft;
  const isStopped = state === CampaignState.Stopped;
  const isPublished = state === CampaignState.Published;
  const isPublishing = state === CampaignState.Publishing;

  // General campaign type
  const isConversation = type === CampaignClass.Conversation;
  const isLandingPage = type === CampaignClass.LandingPage;
  // This is a deprecated lookalike type. The current lookalike type is SeedSegmentCampaign
  const isOldLookalike = type === CampaignClass.Lookalike;
  const isSmartSend = type === CampaignClass.SmartPinpointEmail;
  // Legacy Meta campaign types deprecated in favor of Meta v2
  const isDeprecatedMetaCampaign = !!metaV2 && campaign.isFacebook;

  // Campaign based state
  const isStoppedEmail =
    (type === CampaignClass.AutoPinpointEmail || type === CampaignClass.SmartPinpointEmail) &&
    isStopped;
  const isSingleSendPastStartDate =
    (isPublishing || isPublished) && type === CampaignClass.PinpointEmail && isPastStartDate;
  const hasWizard = !isLandingPage && !isConversation;

  // Logic to allow the display of specific buttons for a campaign
  const allowStop = !isLandingPage && isPublished;
  const allowDelete = isDraft;
  const allowReportLinkInWizardHeader = !isDraft && hasWizard;

  // Disable buttons
  const disableStop = isPastEndDate || isSingleSendPastStartDate;
  const disableRepublish = isPastEndDate || isStoppedEmail;
  const disableEdit = !isDraft;
  const disableDuplicate =
    isOldLookalike || !includedInLicense || isSmartSend || isDeprecatedMetaCampaign;
  const minPackageTooltip = useMinPackageTooltip(campaign.get('_cls'));

  // Determine the tooltip text displayed to the user based on the current state of the campaign
  const getTooltipText = (): {
    disableRepublishTooltip?: string;
    disableStopTooltip?: string;
    disableDuplicateTooltip?: string;
    disableEditTooltip?: string;
  } => {
    let disableRepublishTooltip: string | undefined = undefined;
    let disableStopTooltip: string | undefined = undefined;
    let disableDuplicateTooltip: string | undefined = undefined;
    let disableEditTooltip: string | undefined = undefined;

    if (isPastEndDate) {
      disableRepublishTooltip = text.disableStopOrRepublishPastEndDate;
      disableStopTooltip = text.disableStopOrRepublishPastEndDate;
    }

    if (isStoppedEmail) {
      disableRepublishTooltip = text.disableEmailRepublish;
    }

    if (isSingleSendPastStartDate) {
      disableStopTooltip = text.disableSingleSendStopStartEndDate;
    }

    if (disableDuplicate) {
      if (isOldLookalike) {
        disableDuplicateTooltip = text.disableLookalikeDuplicate;
      } else if (isSmartSend) {
        disableDuplicateTooltip = text.disableDuplicateSmartSend;
      } else if (isDeprecatedMetaCampaign) {
        disableDuplicateTooltip = text.disableDeprecatedMetaDuplicate;
      } else {
        disableDuplicateTooltip = minPackageTooltip;
      }
    }

    if (disableEdit) {
      disableEditTooltip = text.disableEdit;
    }

    return {
      disableRepublishTooltip,
      disableStopTooltip,
      disableDuplicateTooltip,
      disableEditTooltip,
    };
  };

  // Get the tooltip text for the various states
  const {
    disableRepublishTooltip,
    disableStopTooltip,
    disableDuplicateTooltip,
    disableEditTooltip,
  } = getTooltipText();

  return {
    allowed: {
      stop: allowStop,
      delete: allowDelete,
      reportLinkInWizardHeader: allowReportLinkInWizardHeader,
    },
    disabled: {
      stop: disableStop,
      duplicate: disableDuplicate,
      republish: disableRepublish,
      edit: disableEdit,
    },
    hasWizard,
    isDeprecatedMetaCampaign,
    modalHandlers,
    modalStates,
    text: {
      disableRepublish: disableRepublishTooltip,
      disableStop: disableStopTooltip,
      disableEdit: disableEditTooltip,
      disableDuplicate: disableDuplicateTooltip,
    },
    includedInLicense,
  };
}

export default useCampaign;
